import { createContext, useContext, useReducer, useRef, useEffect } from 'react';
import { MatchActionsResponse, School } from 'types/connectTypes';
import {
  ConnectContextType,
  ConnectContextActionType,
  ConnectContextStateInterface,
  ConnectContextProviderInterface,
} from 'features/Connect/features/Context/types';
import {
  ConnectContextActionTypes,
  ConnectModals,
  MatchQuizStatusStrings,
} from 'features/Connect/utils/connectEnums';
import CandidateMatchingAPI from 'features/Connect/api/candidateMatchingAPI';
import { ConnectMatchingAPIViewTypes } from 'features/Connect/api/constants';
import { useRouteMatch } from 'react-router-dom';
import { User } from 'types';
import CandidateMatchQuizAPI from 'features/Connect/api/candidateMatchQuizAPI';
import auth from 'utils/auth';
import UserSavedSchoolAPI from 'features/Connect/api/userSavedSchoolAPI';
import ExpressInterestWaitlistAPI from 'features/Connect/api/expressInterestWaitlistAPI';
import ExpressInterestAPI from 'features/Connect/api/expressInterestAPI';

export const connectContextInitialState: ConnectContextStateInterface = {
  contactedSchoolIds: {},
  connectModalsOpenState: {
    [ConnectModals.AUTHENTICATED_ADMIN_MODAL]: false,
    [ConnectModals.EXPRESS_INTEREST_EXIT_MODAL]: false,
    [ConnectModals.EXPRESS_INTEREST_MODAL]: false,
    [ConnectModals.MATCH_QUIZ_ACCESS_MODAL]: false,
    [ConnectModals.EXPRESS_INTEREST_WAITLIST_MODAL]: false,
    [ConnectModals.READY_TO_CONNECT_MODAL]: false,
    [ConnectModals.QUICK_APPLY_DISTRICT_JOBBOARD_REDIRECT_MODAL]: false,
  },
  isLoading: true,
  isMoreInfoSliderOpen: false,
  isUserAuthenticated: false,
  savedSchoolIds: {},
  waitlistSchoolIds: {},
  selectedSchool: null,
  selectedVacancyId: '0',
  candidateMatches: {
    total: 0,
    pending: [],
    accepted: [],
    rejected: [],
    previously_actioned: [],
  },
  hasInitiallyFetchedMatches: false,
  isFetchingMatches: true,
  isFetchingMatchQuizStatus: false,
  matchQuizStatus: null,
  jobSeekingStatus: null,
  jobSeekingStatusUpdatedAt: null,
  isFetchingSavedSchoolsList: false,
  isFetchingWaitlistSchoolsList: false,
  districtSlugs: {},
};

const connectContextReducer = (
  connectContextState: ConnectContextStateInterface,
  connectContextActions: ConnectContextActionType
): ConnectContextStateInterface => {
  switch (connectContextActions.type) {
    case ConnectContextActionTypes.SET_CANDIDATE_MATCHES:
      return {
        ...connectContextState,
        candidateMatches: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_CONTACTED_SCHOOL_IDS:
      return {
        ...connectContextState,
        contactedSchoolIds: {
          ...connectContextState.contactedSchoolIds,
          [connectContextActions.payload]: true,
        },
      };

    case ConnectContextActionTypes.SET_IS_MODAL_OPEN: {
      const { connectModalName, setIsModalOpen } = connectContextActions.payload;
      return {
        ...connectContextState,
        connectModalsOpenState: {
          ...connectContextState.connectModalsOpenState,
          [connectModalName]: setIsModalOpen,
        },
      };
    }

    case ConnectContextActionTypes.SET_IS_LOADING:
      return {
        ...connectContextState,
        isLoading: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_IS_USER_AUTHENTICATED:
      return {
        ...connectContextState,
        isUserAuthenticated: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_SELECTED_SCHOOL:
      return {
        ...connectContextState,
        selectedSchool: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_SELECTED_VACANCY_ID:
      return {
        ...connectContextState,
        selectedVacancyId: connectContextActions.payload,
      };

    case ConnectContextActionTypes.TOGGLE_SAVED_SCHOOL_IDS: {
      const schoolId = connectContextActions.payload;
      const newSavedSchoolIds = { ...connectContextState.savedSchoolIds };
      if (newSavedSchoolIds[schoolId]) {
        delete newSavedSchoolIds[schoolId];
      } else {
        newSavedSchoolIds[schoolId] = true;
      }
      return {
        ...connectContextState,
        savedSchoolIds: newSavedSchoolIds,
      };
    }
    case ConnectContextActionTypes.SET_SAVED_SCHOOL_IDS:
      return {
        ...connectContextState,
        savedSchoolIds: connectContextActions.payload,
      };
    case ConnectContextActionTypes.SET_IS_FETCHING_MATCHES:
      return {
        ...connectContextState,
        isFetchingMatches: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_HAS_INITIALLY_FETCHED_MATCHES:
      return {
        ...connectContextState,
        hasInitiallyFetchedMatches: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_IS_MORE_INFO_SLIDER_OPEN:
      return {
        ...connectContextState,
        isMoreInfoSliderOpen: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_IS_FETCHING_MATCH_QUIZ_STATUS:
      return {
        ...connectContextState,
        isFetchingMatchQuizStatus: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_MATCH_QUIZ_STATUS:
      return {
        ...connectContextState,
        matchQuizStatus: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_JOB_SEEKING_STATUS:
      return {
        ...connectContextState,
        jobSeekingStatus: connectContextActions.payload.status,
        jobSeekingStatusUpdatedAt: connectContextActions.payload.updated_at,
      };

    case ConnectContextActionTypes.SET_IS_FETCHING_SAVED_SCHOOLS_LIST:
      return {
        ...connectContextState,
        isFetchingSavedSchoolsList: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_WAITLIST_SCHOOL_IDS:
      return {
        ...connectContextState,
        waitlistSchoolIds: connectContextActions.payload,
      };

    case ConnectContextActionTypes.TOGGLE_WAITLIST_SCHOOL_IDS: {
      const schoolId = connectContextActions.payload;
      const newWaitlistSchoolIds = { ...connectContextState.waitlistSchoolIds };
      if (newWaitlistSchoolIds[schoolId]) {
        delete newWaitlistSchoolIds[schoolId];
      } else {
        newWaitlistSchoolIds[schoolId] = true;
      }
      return {
        ...connectContextState,
        waitlistSchoolIds: newWaitlistSchoolIds,
      };
    }

    case ConnectContextActionTypes.SET_IS_FETCHING_WAITLIST_SCHOOLS_LIST:
      return {
        ...connectContextState,
        isFetchingWaitlistSchoolsList: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_DISTRICT_SLUG:
      return {
        ...connectContextState,
        districtSlugs: {
          ...connectContextState.districtSlugs,
          [connectContextActions.payload.ncesId]: connectContextActions.payload.slug,
        },
      };

    default:
      return connectContextState;
  }
};

const ConnectContext = createContext<ConnectContextType | undefined>(undefined);

export const ConnectContextProvider: React.FC<ConnectContextProviderInterface> = ({ children }) => {
  const user = auth.getUser();
  const isUserAuthenticated = auth.loggedIn();
  const [connectContextState, dispatch] = useReducer(
    connectContextReducer,
    connectContextInitialState
  );

  const isMounted = useRef(true);
  const hasFetchedRef = useRef(false); // Add this to track if we've ever fetched

  const isConnectJobboard = useRouteMatch('/connect/:stateCode');
  const isCandidatePortal = useRouteMatch('/candidate/');
  const shouldFetchMatchesAndQuizStatusAndSavedSchools = !!isConnectJobboard || !!isCandidatePortal;

  const connectContextActions = {
    hasSchoolBeenContacted: (schoolId: string): boolean => {
      return !!connectContextState.contactedSchoolIds[schoolId];
    },
    hasSchoolBeenSaved: (schoolId: string): boolean => {
      return !!connectContextState.savedSchoolIds[schoolId];
    },
    hasSchoolBeenWaitlisted: (schoolId: string): boolean => {
      return !!connectContextState.waitlistSchoolIds[schoolId];
    },
    setCandidateMatches: (candidateMatches: MatchActionsResponse) =>
      dispatch({
        type: ConnectContextActionTypes.SET_CANDIDATE_MATCHES,
        payload: candidateMatches,
      }),
    setContactedSchoolIds: (schoolId: string) =>
      dispatch({ type: ConnectContextActionTypes.SET_CONTACTED_SCHOOL_IDS, payload: schoolId }),
    setModalOpen: (connectModalName: string, setIsModalOpen: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_MODAL_OPEN,
        payload: { connectModalName, setIsModalOpen },
      }),
    setIsLoading: (isLoading: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_LOADING,
        payload: isLoading,
      }),
    setIsUserAuthenticated: (isAuthenticated: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_USER_AUTHENTICATED,
        payload: isAuthenticated,
      }),
    setSavedSchoolIds: (savedSchoolIds: Record<string, boolean>) =>
      dispatch({
        type: ConnectContextActionTypes.SET_SAVED_SCHOOL_IDS,
        payload: savedSchoolIds,
      }),
    setWaitlistSchoolIds: (waitlistSchoolIds: Record<string, boolean>) =>
      dispatch({
        type: ConnectContextActionTypes.SET_WAITLIST_SCHOOL_IDS,
        payload: waitlistSchoolIds,
      }),
    setIsFetchingWaitlistSchoolsList: (isLoading: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_FETCHING_WAITLIST_SCHOOLS_LIST,
        payload: isLoading,
      }),
    toggleWaitlistSchoolIds: (schoolId: string) =>
      dispatch({ type: ConnectContextActionTypes.TOGGLE_WAITLIST_SCHOOL_IDS, payload: schoolId }),
    setSelectedSchool: (school: School) =>
      dispatch({
        type: ConnectContextActionTypes.SET_SELECTED_SCHOOL,
        payload: school,
      }),
    setSelectedVacancyId: (selectedVacancyId: string) =>
      dispatch({
        type: ConnectContextActionTypes.SET_SELECTED_VACANCY_ID,
        payload: selectedVacancyId,
      }),
    toggleSavedSchoolIds: (schoolId: string) =>
      dispatch({ type: ConnectContextActionTypes.TOGGLE_SAVED_SCHOOL_IDS, payload: schoolId }),
    setHasInitiallyFetchedMatches: (hasFetched: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_HAS_INITIALLY_FETCHED_MATCHES,
        payload: hasFetched,
      }),
    setIsFetchingMatches: (isFetching: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_FETCHING_MATCHES,
        payload: isFetching,
      }),
    setIsMoreInfoSliderOpen: (isOpen: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_MORE_INFO_SLIDER_OPEN,
        payload: isOpen,
      }),
    fetchCandidateMatches: (user: User) => fetchMatches(user),
    setMatchQuizStatus: (matchQuizStatus: string) =>
      dispatch({
        type: ConnectContextActionTypes.SET_MATCH_QUIZ_STATUS,
        payload: matchQuizStatus,
      }),
    setIsFetchingMatchQuizStatus: (isFetching: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_FETCHING_MATCH_QUIZ_STATUS,
        payload: isFetching,
      }),
    setJobSeekingStatus: (jobSeekingStatus: { status: string; updated_at: string }) =>
      dispatch({
        type: ConnectContextActionTypes.SET_JOB_SEEKING_STATUS,
        payload: jobSeekingStatus,
      }),
    setIsFetchingSavedSchoolsList: (isLoading: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_FETCHING_SAVED_SCHOOLS_LIST,
        payload: isLoading,
      }),
    setDistrictSlug: (ncesId: string, slug: string) =>
      dispatch({
        type: ConnectContextActionTypes.SET_DISTRICT_SLUG,
        payload: { ncesId, slug },
      }),
    getDistrictSlug: (ncesId: string) => connectContextState.districtSlugs[ncesId],
  };

  const fetchMatches = async (user: User) => {
    if (user?.profile && shouldFetchMatchesAndQuizStatusAndSavedSchools) {
      hasFetchedRef.current = true;
      connectContextActions.setIsFetchingMatches(true);

      try {
        const viewType = isCandidatePortal
          ? ConnectMatchingAPIViewTypes.FULL
          : ConnectMatchingAPIViewTypes.BASIC;

        const matches = await CandidateMatchingAPI.getMatches(viewType).catch(() => ({
          total: 0,
          pending: [],
          accepted: [],
          rejected: [],
          previously_actioned: [],
        }));

        if (isMounted.current) {
          connectContextActions.setCandidateMatches(matches);
          connectContextActions.setHasInitiallyFetchedMatches(true);
          connectContextActions.setIsUserAuthenticated(!!user.profile);
        }
      } finally {
        connectContextActions.setIsFetchingMatches(false);
      }
    }
  };

  const fetchMatchQuizStatus = async (user: User) => {
    if (user?.id) {
      dispatch({
        type: ConnectContextActionTypes.SET_IS_FETCHING_MATCH_QUIZ_STATUS,
        payload: true,
      });
      try {
        const response = await CandidateMatchQuizAPI.getMatchQuizStatus(user.id);
        const status = response?.status || MatchQuizStatusStrings.NOT_TAKEN;
        if (isMounted.current) {
          dispatch({ type: ConnectContextActionTypes.SET_MATCH_QUIZ_STATUS, payload: status });
        }
      } catch (e) {
        console.error('Error fetching match quiz status:', e);
      } finally {
        dispatch({
          type: ConnectContextActionTypes.SET_IS_FETCHING_MATCH_QUIZ_STATUS,
          payload: false,
        });
      }
    }
  };

  const fetchSavedSchools = () => {
    if (isUserAuthenticated) {
      connectContextActions.setIsFetchingSavedSchoolsList(true);
      UserSavedSchoolAPI.getSchoolsUserHasSaved()
        .then((savedSchools) => {
          const savedSchoolIds: Record<string, boolean> = {};
          savedSchools.forEach((school) => {
            if (school.school_nces_id) {
              savedSchoolIds[school.school_nces_id] = true;
            }
          });
          connectContextActions.setSavedSchoolIds(savedSchoolIds);
        })
        .finally(() => {
          connectContextActions.setIsFetchingSavedSchoolsList(false);
        });
    }
  };

  const fetchWaitlistSchools = () => {
    if (isUserAuthenticated) {
      connectContextActions.setIsFetchingWaitlistSchoolsList(true);
      ExpressInterestWaitlistAPI.getSchoolsUserIsOnWaitlistFor()
        .then((waitlistSchools) => {
          const waitlistSchoolIds: Record<string, boolean> = {};
          waitlistSchools.forEach((school) => {
            if (school.school_nces_id) {
              waitlistSchoolIds[school.school_nces_id] = true;
            }
          });
          connectContextActions.setWaitlistSchoolIds(waitlistSchoolIds);
        })
        .finally(() => {
          connectContextActions.setIsFetchingWaitlistSchoolsList(false);
        });
    }
  };

  const fetchContactedSchools = () => {
    if (isUserAuthenticated) {
      ExpressInterestAPI.getSchoolsUserExpressedInterestIn().then((contactedSchools) => {
        const contactedSchoolIds: Record<string, boolean> = {};
        contactedSchools.forEach((school) => {
          if (school.school_nces_id) {
            contactedSchoolIds[school.school_nces_id] = true;
          }
        });
        // Update the contacted school IDs in the context
        Object.keys(contactedSchoolIds).forEach((schoolId) => {
          connectContextActions.setContactedSchoolIds(schoolId);
        });
      });
    }
  };

  useEffect(() => {
    if (shouldFetchMatchesAndQuizStatusAndSavedSchools) {
      fetchMatchQuizStatus(user);
      fetchSavedSchools();
      fetchWaitlistSchools();
      fetchContactedSchools();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user?.preference?.job_seeking_status && user?.preference?.job_seeking_status_updated_at) {
      connectContextActions.setJobSeekingStatus({
        status: user.preference.job_seeking_status,
        updated_at: user.preference.job_seeking_status_updated_at,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <ConnectContext.Provider value={{ ...connectContextState, connectContextActions }}>
      {children}
    </ConnectContext.Provider>
  );
};

export const useConnectContext = () => {
  const context = useContext(ConnectContext);
  if (context === undefined) {
    throw new Error('useConnectContext must be used within an ConnectContextProvider');
  }
  return context;
};
